/**
 * Contract for POST /api/v1/imagegen/nfc-card — an AI-designed face for a
 * CR80 NFC card (85.6 x 54 mm, the bank-card size of the blank NTAG216
 * cards), with 2 mm bleed on every side at 300 DPI. The model draws the whole
 * face — themed ground, corner ornament, logo (or a wordmark), and the
 * contactless "TAP TO EXPLORE" mark — and leaves one flat white rounded
 * square empty for the client to composite the business QR into, exactly
 * like Poster and Visiting Card.
 *
 * Same /api/v1/imagegen controller family, curator/admin-gated. This file
 * duplicates rather than imports the sibling contracts' shapes (repo
 * convention — see VisitingCardObject.ts) so the endpoints can't drift into
 * each other.
 *
 * Nothing in the request is mandatory: no logo → the business name becomes a
 * wordmark; neither → a text-free themed emblem. `style.instructions` is the
 * curator's design direction and is treated as the primary creative brief.
 */

// ── Request ──

/** LOGO: 0 or 1. REFERENCE: 0 to 3. PRODUCT is rejected server-side. */
export type NfcCardImageRole = "LOGO" | "REFERENCE";

/** Pointer to an image ALREADY uploaded — bucket-relative path, never bytes. */
export interface NfcCardImageInput {
  objectPath: string;
  role: NfcCardImageRole;
}

export interface NfcCardStyle {
  /** Hex color. Omit to let the model derive the ground from the logo. */
  baseBackgroundColor?: string;
  palette?: string[];
  /** Free string, e.g. "RESTAURANT" — steers the ornament theme. */
  listingType?: string;
  /** The curator's design direction — max NFC_CARD_MAX_DIRECTION_CHARS,
   *  rejected (not truncated) server-side beyond that. */
  instructions?: string;
}

export interface NfcCardRequest {
  /** Audit only — the backend does no business lookup. */
  businessId?: string;
  /** Used only when no LOGO is supplied (rendered as a wordmark). */
  businessName?: string;
  images?: NfcCardImageInput[];
  style?: NfcCardStyle;
}

/** Mirrors NfcCardService.MAX_DESIGN_DIRECTION_CHARS. */
export const NFC_CARD_MAX_DIRECTION_CHARS = 1000;
/** Mirrors NfcCardService.MAX_BUSINESS_NAME_CHARS. */
export const NFC_CARD_MAX_BUSINESS_NAME_CHARS = 60;

// ── Response ──

export interface NfcCardImage {
  /** Always JPEG — render with `data:image/jpeg;base64,` + base64. Full
   *  bleed canvas (currently 1059x686); always use width/height from here. */
  base64: string;
  width: number;
  height: number;
}

/** Pixel rect in the image's own coordinate space. */
export interface NfcCardRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface NfcCardContent {
  generationId: string;
  image: NfcCardImage;
  /** reservedZone inset by the quiet zone — fallback only; the client fits
   *  the QR to the detected white square, same as the siblings. */
  qrArea: NfcCardRect;
  /** The flat white rounded square the model left empty. */
  reservedZone: NfcCardRect;
  aiGenerated: boolean;
  promptVersion: string;
  createdAt: string;
}

export interface NfcCardGeneralProcessingStatus {
  processingStatus: "OK" | "ERROR";
  processingDetails: string;
}

export interface NfcCardErrorInformation {
  errorCode: string;
  errorDetail: string;
  additionalDetails: Record<string, any> | null;
}

export interface NfcCardResponse {
  errorInformation: NfcCardErrorInformation[] | null;
  generalProcessingStatus: NfcCardGeneralProcessingStatus;
  /** null on failure. Exactly one entry on success. */
  contents: NfcCardContent[] | null;
}

// ── Error codes ──

/** Confirmed against NfcCardController / CuratorAuthorizationService
 *  .authorizeImageGeneration — the imagegen gate reports AI_IMG_* codes. */
export const NFC_CARD_ERROR_CODES = {
  VALIDATION_FAILED: "AI_IMG_VALIDATION_FAILED",
  UNSUPPORTED_FORMAT: "AI_IMG_UNSUPPORTED_FORMAT",
  MODERATION_BLOCKED: "AI_IMG_MODERATION_BLOCKED",
  UNAUTHENTICATED: "AI_IMG_UNAUTHENTICATED",
  NOT_CURATOR: "AI_IMG_NOT_CURATOR",
  ROLE_CHECK_FAILED: "AI_IMG_ROLE_CHECK_FAILED",
  QUOTA_EXCEEDED: "AI_IMG_QUOTA_EXCEEDED",
  BUSY: "AI_IMG_BUSY",
  PROVIDER_FAILED: "AI_IMG_PROVIDER_FAILED",
  BUDGET_EXCEEDED: "AI_IMG_BUDGET_EXCEEDED",
} as const;

export const NFC_CARD_ERROR_COPY: Record<string, { message: string; retryable: boolean }> = {
  [NFC_CARD_ERROR_CODES.UNSUPPORTED_FORMAT]: {
    message: "The logo can't be used — convert it to JPEG or PNG and try again.",
    retryable: true,
  },
  [NFC_CARD_ERROR_CODES.MODERATION_BLOCKED]: {
    message: "This request couldn't be processed. Try different design direction.",
    retryable: false,
  },
  [NFC_CARD_ERROR_CODES.UNAUTHENTICATED]: {
    message: "Your session has expired. Please log in again.",
    retryable: false,
  },
  [NFC_CARD_ERROR_CODES.NOT_CURATOR]: {
    message: "Only curators can use this tool.",
    retryable: false,
  },
  [NFC_CARD_ERROR_CODES.ROLE_CHECK_FAILED]: {
    message: "Couldn't verify your role right now. Please try again.",
    retryable: true,
  },
  [NFC_CARD_ERROR_CODES.QUOTA_EXCEEDED]: {
    message: "Daily AI image limit reached. Try again tomorrow.",
    retryable: false,
  },
  [NFC_CARD_ERROR_CODES.BUSY]: {
    message: "The AI tool is busy right now. Please try again in a moment.",
    retryable: true,
  },
  [NFC_CARD_ERROR_CODES.PROVIDER_FAILED]: {
    message: "Something went wrong generating that NFC card. Please try again.",
    retryable: true,
  },
  [NFC_CARD_ERROR_CODES.BUDGET_EXCEEDED]: {
    message: "That took too long to generate. Please try again.",
    retryable: true,
  },
};

/** VALIDATION_FAILED is deliberately not in the table: its server detail
 *  (e.g. "Design direction exceeds 1000 characters") is the useful message. */
export function nfcCardErrorCopy(
  errorCode: string | null | undefined,
  errorDetail?: string | null
): { message: string; retryable: boolean } {
  if (errorCode && NFC_CARD_ERROR_COPY[errorCode]) {
    return NFC_CARD_ERROR_COPY[errorCode];
  }
  return {
    message: errorDetail || "Couldn't generate that NFC card. Try again.",
    retryable: true,
  };
}
